import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Download } from "lucide-react";
import { RESUME_URL } from "@/lib/api";

const links = [
  { to: "/work", label: "Work" },
  { to: "/playground", label: "Playground" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled ? "backdrop-blur-xl bg-black/60 border-b border-white/10" : "bg-transparent border-b border-transparent"
      }`}
    >
      <nav className="mx-auto max-w-7xl px-6 lg:px-10 h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid="nav-logo"
          className="font-heading text-lg tracking-tight text-cream hover:text-white transition-colors"
        >
          Nishant Gathwal<span className="text-ash">.</span>
        </Link>

        <div className="hidden md:flex items-center gap-9">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-sm tracking-wide transition-colors ${
                  isActive ? "text-cream" : "text-ash hover:text-cream"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <a
            href={RESUME_URL}
            data-testid="nav-resume-button"
            className="inline-flex items-center gap-2 rounded-full bg-cream text-ink text-sm font-medium px-5 py-2.5 hover:bg-white transition-colors"
          >
            <Download size={15} strokeWidth={2} />
            Resume
          </a>
        </div>

        <button
          onClick={() => setOpen((v) => !v)}
          data-testid="nav-mobile-toggle"
          className="md:hidden text-cream p-2 -mr-2"
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </nav>

      {open && (
        <div data-testid="mobile-menu" className="md:hidden bg-black/95 backdrop-blur-xl border-t border-white/10 px-6 py-6 space-y-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`mobile-link-${l.label.toLowerCase()}`}
              className="block py-3 text-lg font-heading text-cream border-b border-white/5"
            >
              {l.label}
            </NavLink>
          ))}
          <a
            href={RESUME_URL}
            data-testid="mobile-resume-button"
            className="mt-4 inline-flex items-center gap-2 rounded-full bg-cream text-ink text-sm font-medium px-5 py-2.5"
          >
            <Download size={15} /> Download Resume
          </a>
        </div>
      )}
    </header>
  );
};

export default Navbar;
